import React, { useState, useEffect, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '@store/hooks';
import { resendSignUpCode, resetPassword } from 'aws-amplify/auth';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    RESET_PASSWORD_PATH,
    LOGIN_PATH,
    VERIFICATION_PATH,
} from '@constants/routes';
import { setLoginEmail } from '@store/reducers/auth/loginSlice';
import AuthLayout from '@components/Layout/auth/AuthLayout';
import ErrorMessage from '@components/Error/ErrorMessage';
import LoadingSpinner from '@components/Spinner';
import { setEmail, setShowLink } from '@store/reducers/auth/signupSlice';
import { userVerification } from '@utils/sharedUtils';
import { API_KEY } from '@constants/AppConfig';
import { FORGOT_PASSWORD_API } from '@constants/auth/Api';
import { DRIPSET_LOGO } from '@constants/images';
import { FiMail, FiArrowRight, FiLock, FiClock, FiShield } from 'react-icons/fi';

const ForgotPassword = () => {
    const [userEmail, setUserEmail] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const loginEmail = useAppSelector((state) => state.login.email);

    const handleBeforeUnload = useCallback((event: BeforeUnloadEvent) => {
        const message = 'Are you sure you want to leave?';
        event.preventDefault();
        event.returnValue = message;
        return message;
    }, []);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = '';
            document.documentElement.style.overflow = '';
        };
    }, []);

    useEffect(() => {
        window.addEventListener('beforeunload', handleBeforeUnload);

        setUserEmail(loginEmail);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [loginEmail, handleBeforeUnload]);

    const handleUserEmailChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setUserEmail(event.target.value);
        setError(null);
    };

    const handleForgotPassword = async (event: React.SyntheticEvent) => {
        event.preventDefault();
        setIsLoading(true);
        setError(null);
        
        try {
            const response = await fetch(FORGOT_PASSWORD_API, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': API_KEY,
                },
                body: JSON.stringify({
                    email: userEmail,
                }),
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.message || 'Failed to send forgot password request');
            }

            dispatch(setLoginEmail(userEmail));
            navigate(RESET_PASSWORD_PATH);
        } catch (error: any) {
            // console.log(error);
            let errorMessage = 'Internal Server Error';

            switch (error.name) {
                case 'UserNotFoundException':
                case 'NotAuthorizedException':
                    errorMessage = 'Invalid credential.';
                    break;
                case 'UserNotConfirmedException':
                    await resendSignUpCode({
                        username: userEmail,
                    });
                    dispatch(setEmail(userEmail));
                    dispatch(setShowLink(false));
                    navigate(VERIFICATION_PATH);
                    errorMessage = 'User not Verified.';
                case 'TooManyRequestsException':
                    errorMessage = 'Too many requests. Please try again later.';
                    break;
                case 'InvalidParameterException':
                    errorMessage = 'Invalid parameters provided.';
                    break;
                default:
                    errorMessage = error.message || 'An unknown error occurred';
                    break;
            }

            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleBackClick = () => {
        navigate(LOGIN_PATH);
    };

    return (
        <AuthLayout
            onBackClick={handleBackClick}
            className="relative !min-h-0 h-screen max-h-screen bg-cover bg-center overflow-hidden flex flex-col justify-between"
            style={{
                background: "url('/public/images/reset_password_bg.png') no-repeat center center / 100% 100%",
            }}
            hideBrandHeader={true}
            hideEmsenseLogo={true}
            backButtonClassName="!text-[#0346A2] font-bold left-6 md:left-10 mt-6"
        >
            <div className="flex-grow flex flex-col items-center justify-between py-2 px-4 z-10 w-full relative h-[calc(100vh-60px)] min-h-0 overflow-y-auto">
                {/* Top Section wrapper for Logo and Card */}
                <div className="flex flex-col items-center w-full justify-center flex-grow min-h-0">
                    {/* DripSet Logo */}
                    <div className="flex justify-center mb-8 shrink-0">
                        <img
                            src={DRIPSET_LOGO}
                            alt="DripSet"
                            className="h-16 object-contain"
                        />
                    </div>

                    {/* Card */}
                    <div className="card w-full max-w-[580px] border-none rounded-3xl bg-white shadow-2xl p-6 sm:p-8 relative shrink min-h-0 overflow-visible">
                        {/* Top Circle Icon with Lock and Dot Grids */}
                        <div className="flex justify-center -mt-16 mb-4 relative items-center shrink-0">
                            {/* Left Dot Grid */}
                            <svg width="32" height="10" viewBox="0 0 40 12" className="absolute right-[calc(50%+56px)] top-1/2 -translate-y-1/2 opacity-30">
                                <circle cx="4" cy="3" r="1.5" fill="#0346A2" />
                                <circle cx="12" cy="3" r="1.5" fill="#0346A2" />
                                <circle cx="20" cy="3" r="1.5" fill="#0346A2" />
                                <circle cx="28" cy="3" r="1.5" fill="#0346A2" />
                                <circle cx="36" cy="3" r="1.5" fill="#0346A2" />
                                <circle cx="4" cy="9" r="1.5" fill="#0346A2" />
                                <circle cx="12" cy="9" r="1.5" fill="#0346A2" />
                                <circle cx="20" cy="9" r="1.5" fill="#0346A2" />
                                <circle cx="28" cy="9" r="1.5" fill="#0346A2" />
                                <circle cx="36" cy="9" r="1.5" fill="#0346A2" />
                            </svg>

                            {/* Lock Circle */}
                            <div className="w-20 h-20 rounded-full bg-gradient-to-b from-[#dbe8fd] to-[#f0f6ff] border-[5px] border-white shadow-[0_4px_15px_rgba(0,0,0,0.08)] flex items-center justify-center relative">
                                <svg viewBox="0 0 24 24" className="w-12 h-12 text-[#0346A2]">
                                    {/* Lock Shackle */}
                                    <path d="M7 10V7a5 5 0 0 1 10 0v3" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                    
                                    {/* Lock Body */}
                                    <rect x="3" y="10" width="18" height="11" rx="2.5" ry="2.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                    
                                    {/* Keyhole */}
                                    <circle cx="12" cy="14.5" r="1.5" fill="currentColor" />
                                    <path d="M12 16v2" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                                    
                                    {/* White Droplet Mask */}
                                    <path d="M18.5 13.5c0 0 3 3.5 3 5.5a3 3 0 1 1-6 0c0-2 3-5.5 3-5.5z" fill="white" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
                                    
                                    {/* Blue Droplet */}
                                    <path d="M18.5 13.5c0 0 3 3.5 3 5.5a3 3 0 1 1-6 0c0-2 3-5.5 3-5.5z" fill="currentColor" />
                                </svg>
                            </div>

                            {/* Right Dot Grid */}
                            <svg width="32" height="10" viewBox="0 0 40 12" className="absolute left-[calc(50%+56px)] top-1/2 -translate-y-1/2 opacity-30">
                                <circle cx="4" cy="3" r="1.5" fill="#0346A2" />
                                <circle cx="12" cy="3" r="1.5" fill="#0346A2" />
                                <circle cx="20" cy="3" r="1.5" fill="#0346A2" />
                                <circle cx="28" cy="3" r="1.5" fill="#0346A2" />
                                <circle cx="36" cy="3" r="1.5" fill="#0346A2" />
                                <circle cx="4" cy="9" r="1.5" fill="#0346A2" />
                                <circle cx="12" cy="9" r="1.5" fill="#0346A2" />
                                <circle cx="20" cy="9" r="1.5" fill="#0346A2" />
                                <circle cx="28" cy="9" r="1.5" fill="#0346A2" />
                                <circle cx="36" cy="9" r="1.5" fill="#0346A2" />
                            </svg>
                        </div>

                        <div className="card-body px-3 py-1">
                            {/* Title */}
                            <h3 className="text-2xl font-extrabold text-[#0B0B0B] text-center mt-1 mb-0.5">
                                Forgot <span className="text-[#0346A2]">Password?</span>
                            </h3>

                            {/* Divider Line */}
                            <div className="w-10 h-1 bg-[#0346A2] mx-auto my-2 rounded-full" />

                            {/* Description */}
                            <p className="text-[#7D7A7A] text-[13px] text-center leading-normal max-w-sm mx-auto mb-4">
                                Enter the email associated with your account and we'll send code to your email to reset your password.
                            </p>

                            {error && <ErrorMessage message={error} />}

                            <form onSubmit={handleForgotPassword}>
                                <div className="mb-3">
                                    <label
                                        htmlFor="email"
                                        className="block text-xs font-semibold text-[#3D3D3D] mb-1.5"
                                    >
                                        Your Email ID
                                    </label>
                                    <div className="relative flex items-center">
                                        <FiMail className="absolute left-4 text-lg text-[#0346A2]" />
                                        <input
                                            type="text"
                                            className="w-full h-11 pl-11 pr-4 border border-[#D9D9D9] rounded-xl focus:border-[#0346A2] focus:ring-1 focus:ring-[#0346A2] outline-none transition text-[#161616] placeholder-[#D9D9D9] text-sm font-medium"
                                            id="email"
                                            name="email"
                                            value={userEmail}
                                            onChange={handleUserEmailChange}
                                            placeholder="Enter Your Email ID"
                                            autoFocus
                                            autoComplete="off"
                                        />
                                    </div>
                                </div>

                                <div className="mt-5">
                                    {isLoading ? (
                                        <div className="relative flex items-center justify-center w-full h-11 bg-gradient-to-r from-[#0346A2] to-[#1e6ae6] text-white font-bold rounded-xl transition shadow-lg">
                                            <LoadingSpinner className="text-white" />
                                        </div>
                                    ) : (
                                        <button
                                            type="submit"
                                            className="relative flex items-center justify-center w-full h-11 bg-gradient-to-r from-[#0346A2] to-[#1e6ae6] hover:opacity-95 text-white font-bold rounded-xl transition shadow-lg text-sm"
                                        >
                                            <span>Send Reset Code</span>
                                            <div className="absolute right-4 w-6.5 h-6.5 rounded-full bg-white/20 flex items-center justify-center">
                                                <FiArrowRight className="text-white text-sm" />
                                            </div>
                                        </button>
                                    )}
                                </div>

                                {/* Security text */}
                                <div className="flex items-center justify-center gap-1.5 mt-3.5 text-[#7D7A7A] text-[11px] font-semibold">
                                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5 text-[#0346A2]">
                                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                                    </svg>
                                    <span>We never share your email with anyone.</span>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>

                {/* Bottom Row Features Card */}
                <div className="w-full max-w-[900px] bg-white rounded-[24px] shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#EAF0F9] p-4 flex flex-row items-center justify-between gap-4 mx-auto mt-2 shrink-0 z-10 hidden sm:flex px-8">
                    {/* Feature 1 */}
                    <div className="flex items-center gap-3.5 w-full md:w-auto">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#2f80ed] to-[#0449a5] flex items-center justify-center text-white shrink-0 shadow-[0_4px_12px_rgba(47,128,237,0.25)]">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                                <path d="m9 11 2 2 4-4" />
                            </svg>
                        </div>
                        <div>
                            <h4 className="text-[13px] font-bold text-[#0346A2] mb-0.5">Secure & Private</h4>
                            <p className="text-[11px] text-[#4E5D78] mb-0 font-medium leading-tight">Your information is always protected with us.</p>
                        </div>
                    </div>

                    <div className="w-[1px] h-9 bg-[#e0ebfd] shrink-0" />

                    {/* Feature 2 */}
                    <div className="flex items-center gap-3.5 w-full md:w-auto">
                        <div className="w-14 h-14 rounded-2xl bg-[#eaf2ff] flex items-center justify-center text-[#0346A2] shrink-0">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0346A2" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
                                <path d="M2 9h5" />
                                <path d="M1 12h7" />
                                <path d="M2 15h5" />
                                <circle cx="16" cy="12" r="5" />
                                <path d="M16 9v3l2 1" />
                            </svg>
                        </div>
                        <div>
                            <h4 className="text-[13px] font-bold text-[#0346A2] mb-0.5">Quick & Easy</h4>
                            <p className="text-[11px] text-[#4E5D78] mb-0 font-medium leading-tight">Reset your password in just a few steps.</p>
                        </div>
                    </div>

                    <div className="w-[1px] h-9 bg-[#e0ebfd] shrink-0" />

                    {/* Feature 3 */}
                    <div className="flex items-center gap-3.5 w-full md:w-auto">
                        <div className="w-14 h-14 rounded-2xl bg-[#eaf2ff] flex items-center justify-center text-[#0346A2] shrink-0">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0346A2" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
                                <rect x="3" y="5" width="18" height="14" rx="3" />
                                <path d="M3 7l9 6 9-6" />
                            </svg>
                        </div>
                        <div>
                            <h4 className="text-[13px] font-bold text-[#0346A2] mb-0.5">Email Verification</h4>
                            <p className="text-[11px] text-[#4E5D78] mb-0 font-medium leading-tight">We'll send a secure code to your email.</p>
                        </div>
                    </div>
                </div>

            </div>
        </AuthLayout>
    );
};

export default ForgotPassword;
